import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import { useApi } from "../../api/useApiRedux";
import ErrorMessages from "../../components/Notifications/ErrorMessages";

const Container = styled.div`
  padding: 1rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  margin-bottom: 1rem;
  color: ${(props) => props.theme.onSurface};
`;

const Description = styled.p`
  margin-bottom: 1rem;
  color: ${(props) => props.theme.onSurfaceAlt};
`;

const ConfirmContent = styled.div`
  padding: 1rem;
`;

const ConfirmText = styled.p`
  margin-bottom: 1.5rem;
  font-size: 1rem;
  color: ${(props) => props.theme.onSurface};
`;

const WarningText = styled.p`
  margin-bottom: 1rem;
  font-weight: bold;
  color: ${(props) => props.theme.error};
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const ResultMessage = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 4px;
  background-color: ${(props) =>
    props.success ? props.theme.success + "20" : props.theme.error + "20"};
  color: ${(props) => (props.success ? props.theme.success : props.theme.error)};
`;

const InputGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${(props) => props.theme.onSurface};
  font-weight: 500;
`;

const InputWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const Input = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid ${(props) => props.theme.border || "#ccc"};
  border-radius: 4px;
  background-color: ${(props) => props.theme.surface};
  color: ${(props) => props.theme.onSurface};
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.primary};
  }
`;

const ShowButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${(props) => props.theme.border || "#ccc"};
  border-radius: 4px;
  background-color: ${(props) => props.theme.surface};
  color: ${(props) => props.theme.onSurface};
  cursor: pointer;
  font-size: 0.875rem;
  white-space: nowrap;

  &:hover {
    background-color: ${(props) => props.theme.surfaceHover || "#f0f0f0"};
  }

  &:active {
    background-color: ${(props) => props.theme.surfaceActive || "#e0e0e0"};
  }
`;

const DevControls = () => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [result, setResult] = useState(null);
  const [authKey, setAuthKey] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [{ errors, loading, data }, doCancelAll] = useApi();

  useEffect(() => {
    if (data && !errors) {
      setResult({
        success: true,
        message: `Successfully cancelled ${data.length} active report run(s)`,
        data: data,
      });
      setShowConfirmModal(false);
    }
  }, [data, errors]);

  useEffect(() => {
    if (errors) {
      setResult({
        success: false,
        message: `Failed to cancel report runs`,
      });
      setShowConfirmModal(false);
    }
  }, [errors]);

  const handleCancelAllReports = () => {
    setResult(null);
    doCancelAll({
      query: `/developers/endAllActiveSummaries`,
      method: "POST",
      options: {
        secure: true,
        headers: {
          authKey: authKey,
        },
      },
    });
  };

  return (
    <Container>
      <Section>
        <SectionTitle>Report Management</SectionTitle>
        <InputGroup>
          <Label htmlFor="authKey">Authentication Key</Label>
          <InputWrapper>
            <Input
              id="authKey"
              type={showPassword ? "text" : "password"}
              value={authKey}
              onChange={(e) => setAuthKey(e.target.value)}
              placeholder="Enter authentication key"
            />
            <ShowButton
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "Hide" : "Show"}
            </ShowButton>
          </InputWrapper>
        </InputGroup>
        <Description>
          Cancel all currently active data quality report runs. This will set
          all active summaries to a cancelled state.
        </Description>
        <Button
          type="button"
          onClick={() => setShowConfirmModal(true)}
          disabled={loading || !authKey}
        >
          Cancel All Report Runs
        </Button>
        {errors && <ErrorMessages errors={errors} />}
        {result && (
          <ResultMessage success={result.success}>
            {result.message}
          </ResultMessage>
        )}
      </Section>

      {showConfirmModal && (
        <Modal
          title="Confirm Cancel All Report Runs"
          hide={() => setShowConfirmModal(false)}
        >
          <ConfirmContent>
            <WarningText>Warning: This action cannot be undone!</WarningText>
            <ConfirmText>
              Are you sure you want to cancel all active report runs? This will
              affect all currently running data quality reports.
            </ConfirmText>
            <ModalActions>
              <Button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCancelAllReports}
                disabled={loading}
              >
                {loading ? "Cancelling..." : "Confirm"}
              </Button>
            </ModalActions>
          </ConfirmContent>
        </Modal>
      )}
    </Container>
  );
};

export default DevControls;
